import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import MenuItem from '@mui/material/MenuItem'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import InputBase from '@mui/material/InputBase'
import SearchIcon from '@mui/icons-material/Search'
import Title from '../../component/Title'
import ResetFilterButton from '../../component/ResetFilterButton'
import { LIST_FILTER_SEARCH_FIELD_SX, LIST_FILTER_SEARCH_BUTTON_SX, ListFilterBar, ListFilterForm } from '../../component/ListFilterLayout'
import API from '../../component/Axios'
import Pagination from '../../component/Pagination'
import EditIcon from '../../component/EditIcon'
import ListActionGroup from '../../component/ListActionGroup'
import ListEmptyState from '../../component/ListEmptyState'
import ListTableContainer from '../../component/ListTableContainer'
import Snackbars from '../../component/Snackbars'
import Spiner from '../../component/Spiner'
import { buildListSearch as buildListSearchQuery, createInitialListState, parseNumberParam } from '../../utils/listPageState'
import { renderDateTime } from '../../utils/dateDisplay'

const DEFAULT_SEARCH_SELECT = {
    searchType: 0,
}

const SEARCH_SELECT_PARAMS = {
    searchType: {
        param: 'type',
        defaultValue: DEFAULT_SEARCH_SELECT.searchType,
        parse: (value, defaultValue) => parseNumberParam(value, defaultValue),
    },
}

const APP_RUNNING_TYPE_LABELS = {
    1: '거리전',
    2: '속도전',
    3: '애니멀런',
    4: '우리끼리',
}

const APP_RUNNING_TABLE_COLUMNS = [
    { width: 70 },
    { width: 180 },
    { width: 110 },
    { width: 100 },
    { width: 100 },
    { width: 120 },
    { width: 120 },
    { width: 180 },
    { width: 180 },
    { width: 120 },
]

const APP_RUNNING_TABLE_MIN_WIDTH = APP_RUNNING_TABLE_COLUMNS.reduce((sum, column) => sum + column.width, 0)

const APP_RUNNING_TEXT_CELL_SX = {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
}

const APP_RUNNING_LABEL_CELL_SX = {
    whiteSpace: 'nowrap',
}

const getAppRunningTypeLabel = (type) => APP_RUNNING_TYPE_LABELS[Number(type)] || '우리끼리'

export default function AppRunnings({ adminId, role }) {
    const location = useLocation()
    const history = useHistory()
    const initialListStateRef = useRef(createInitialListState({
        location,
        defaultSearchSelect: DEFAULT_SEARCH_SELECT,
        selectParams: SEARCH_SELECT_PARAMS,
    }))
    const initialListState = initialListStateRef.current
    const silentListRefreshRef = useRef(false)
    const [loading, setLoading] = useState(true)
    const [totalCount, setTotalCount] = useState(0)
    const [params, setParams] = useState([])
    const [curPage, setCurPage] = useState(initialListState.curPage)
    const offset = 10
    const [openSnackbar, setOpenSnackbar] = useState(initialListState.snackbar.openSnackbar)
    const [severitySnackbar, setSeveritySnackbar] = useState(initialListState.snackbar.severitySnackbar)
    const [messageSnackbar, setMessageSnackbar] = useState(initialListState.snackbar.messageSnackbar)
    const [searchText, setSearchText] = useState(initialListState.searchText)
    const [searchQuery, setSearchQuery] = useState(initialListState.searchQuery)
    const [searchSelect, setSearchSelect] = useState(initialListState.searchSelect)
    const [listErrorMessage, setListErrorMessage] = useState('')

    const getData = useCallback(async (showSpinner = true) => {
        if (showSpinner) {
            setLoading(true)
        }
        setListErrorMessage('')

        try {
            const { data } = await API.post('/api/v1/admin/appRunning/getList', {
                page: curPage, offset: offset, adminId: adminId, role: role, searchText: searchQuery, searchSelect: searchSelect
            })

            if (data.code === 200) {
                setTotalCount(data.data.count || 0)
                setParams(data.data.rows || [])
                setListErrorMessage('')
            } else if (data.code === 403) {
                history.push('/login')
                return
            } else {
                const errorMessage = data.message || 'App 러닝 목록을 불러오지 못했습니다.'
                setTotalCount(0)
                setParams([])
                setListErrorMessage(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
            }
        } catch (_error) {
            const errorMessage = 'App 러닝 목록을 불러오지 못했습니다.'
            setTotalCount(0)
            setParams([])
            setListErrorMessage(errorMessage)
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(errorMessage)
        } finally {
            if (showSpinner) {
                setLoading(false)
            }
        }
    }, [adminId, curPage, history, role, searchQuery, searchSelect])

    useEffect(() => {
        const showSpinner = !silentListRefreshRef.current
        silentListRefreshRef.current = false
        getData(showSpinner)
    }, [getData])

    useEffect(() => {
        const nextSearch = buildListSearchQuery({
            curPage,
            searchQuery,
            searchSelect,
            selectParams: SEARCH_SELECT_PARAMS,
        })

        if (location.search !== nextSearch) {
            history.replace({
                pathname: location.pathname,
                search: nextSearch,
            })
        }
    }, [curPage, history, location.pathname, location.search, searchQuery, searchSelect])

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    const updateSearchSelect = (e) => {
        if (loading) {
            return
        }

        if (curPage === 1) {
            silentListRefreshRef.current = true
        }

        setSearchSelect({
            ...searchSelect,
            [e.target.name]: e.target.value
        })
        setSearchQuery((searchText || '').trim())
        setCurPage(1)
    }

    const resetFilters = () => {
        if (loading) {
            return
        }

        setCurPage(1)
        setSearchText('')
        setSearchQuery('')
        setSearchSelect({
            ...DEFAULT_SEARCH_SELECT,
        })
    }

    // 입력값과 실제 조회값을 분리해 Enter/검색 버튼 시점만 제어한다.
    const searchButtonClick = () => {
        if (loading) {
            return
        }

        const trimmedSearchText = (searchText || '').trim()

        if (curPage === 1 && searchQuery === trimmedSearchText) {
            getData(false)
            return
        }

        setSearchQuery(trimmedSearchText)

        if (curPage !== 1) {
            setCurPage(1)
            return
        }

        if (searchQuery !== trimmedSearchText) {
            return
        }

        getData(false)
    }

    const handleSearchSubmit = (e) => {
        e.preventDefault()
        searchButtonClick()
    }

    const hasActiveFilters = Boolean((searchQuery || '').trim())
        || String(searchSelect.searchType) !== String(DEFAULT_SEARCH_SELECT.searchType)

    const renderEmptyState = () => {
        if (listErrorMessage) {
            return (
                <ListEmptyState
                    variant="error"
                    title="App 러닝 목록을 불러오지 못했습니다."
                    description={listErrorMessage}
                    action={(
                        <Button
                            type="button"
                            size="small"
                            variant="outlined"
                            onClick={() => getData(false)}
                            disabled={loading}
                        >
                            다시 조회
                        </Button>
                    )}
                />
            )
        }

        if (hasActiveFilters) {
            return (
                <ListEmptyState
                    title="조건에 맞는 App 러닝 기록이 없습니다."
                    description="검색어 또는 필터를 초기화한 뒤 다시 확인해보세요."
                    action={<ResetFilterButton label="필터 초기화" onClick={resetFilters} disabled={loading} />}
                />
            )
        }

        return (
            <ListEmptyState
                title="조회된 App 러닝 기록이 없습니다."
                description="러닝 기록이 등록되면 이 목록에 표시됩니다."
            />
        )
    }

    return (
        <Grid item xs={12}>
            {loading ? (
                <Spiner/>
            ) : (
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', width: '100%' }}>
                    {/* 타이틀 */}
                    <Title>App 러닝 리스트</Title>
                    {/* 검색폼 */}
                    <ListFilterBar>
                        <ListFilterForm onSubmit={handleSearchSubmit} trailing={<ResetFilterButton onClick={resetFilters} disabled={loading} />}>
                            <InputBase
                                sx={LIST_FILTER_SEARCH_FIELD_SX}
                                placeholder="이름/닉네임"
                                type="search" 
                                name="searchText"
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                disabled={loading}
                            />
                            <IconButton type="submit" sx={LIST_FILTER_SEARCH_BUTTON_SX} aria-label="search" disabled={loading}><SearchIcon /></IconButton>
                            <Divider sx={{ height: 28, m: 0.5, marginRight:2 }} orientation="vertical" />
                            <TextField
                                label="타입"
                                select
                                name="searchType"
                                value={searchSelect.searchType}
                                onChange={updateSearchSelect}
                                size="small"
                                disabled={loading}
                                sx={{ minWidth: 150 }}
                            >
                                <MenuItem value={0}>전체</MenuItem>
                                <MenuItem value={1}>거리전</MenuItem>
                                <MenuItem value={2}>속도전</MenuItem>
                                <MenuItem value={3}>애니멀런</MenuItem>
                                <MenuItem value={4}>우리끼리</MenuItem>
                            </TextField>
                        </ListFilterForm>
                    </ListFilterBar>
                    {/* 테이블 */}
                    <ListTableContainer>
                    <Table size="small" sx={{ minWidth: APP_RUNNING_TABLE_MIN_WIDTH, tableLayout: 'fixed' }}>
                        <colgroup>
                            {APP_RUNNING_TABLE_COLUMNS.map((column, index) => (
                                <col key={`app-running-column-${index}`} style={{ width: column.width }} />
                            ))}
                        </colgroup>
                        <caption>Total Count: {totalCount}</caption>
                        <TableHead>
                            <TableRow>
                                <TableCell>No</TableCell>
                                <TableCell>이름<br/>닉네임</TableCell>
                                <TableCell>RunId</TableCell>
                                <TableCell>타입</TableCell>
                                <TableCell>코스</TableCell>
                                <TableCell>거리</TableCell>
                                <TableCell>시간</TableCell>
                                <TableCell>등록일</TableCell>
                                <TableCell>수정일</TableCell>
                                <TableCell align="right">Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {params.length > 0 ? (
                                params.map((row) => (
                                    <TableRow key={row.userRunId}>
                                        <TableCell>{row.rownum}</TableCell>
                                        <TableCell sx={APP_RUNNING_TEXT_CELL_SX}>{row.user?.username || '-'}<br/>{row.user?.nickName || '-'}</TableCell>
                                        <TableCell sx={APP_RUNNING_LABEL_CELL_SX}>{row.userRunId || '-'}</TableCell>
                                        <TableCell sx={APP_RUNNING_LABEL_CELL_SX}>{getAppRunningTypeLabel(row.type)}</TableCell>
                                        <TableCell sx={APP_RUNNING_LABEL_CELL_SX}>{row.meetDistance || '-'}</TableCell>
                                        <TableCell sx={APP_RUNNING_LABEL_CELL_SX}>{row.totalDistance || '-'}</TableCell>
                                        <TableCell sx={APP_RUNNING_LABEL_CELL_SX}>{row.totalTime || '-'}</TableCell>
                                        <TableCell sx={APP_RUNNING_LABEL_CELL_SX}>{renderDateTime(row.regTime)}</TableCell>
                                        <TableCell sx={APP_RUNNING_LABEL_CELL_SX}>{renderDateTime(row.updTime)}</TableCell>
                                        <TableCell align="right">
                                            <ListActionGroup>
                                                <EditIcon path={'appRunning'} id={row.userRunId} type={row.type} search={location.search} searchText={searchQuery} searchSelect={searchSelect} curPage={curPage} disabled={loading}/>
                                            </ListActionGroup>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={10}>
                                        {renderEmptyState()}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                    </ListTableContainer>
                    {totalCount > 0 && <Pagination curPage={curPage} offset={offset} totalCount={totalCount} setCurPage={setCurPage} />}
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
                </Paper>
            )}
        </Grid>
    )
}
